import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Moment } from 'moment'
import moment from 'moment'

import { isAlreadyHaveEventWithThatTime } from 'features/BookingCalendarController/model/hooks/useCalendarActions'
import {
    ViewMode,
    ViewModeType,
} from 'features/BookingCalendarController/model'
import { Counterparty, CounterpartyType } from 'entities/counterparties/model'
import { Area, TimePeriod } from 'entities/areas/model'
import { Booking, useGetBookingsListQuery } from 'entities/bookings/model'
import { OrderBooking } from 'entities/orders/model'

import {
    selectActiveBookingId,
    selectBookingCounterparty,
    selectBookingOrderId,
    selectBookings,
    selectTimeFilter,
    setCurrentCalendarEvents,
} from '../../../Booking/model/slices'
import { CalendarEvent } from '../../../ReactBigCalendar/ReactBigCalendar'
import { utlToLocal } from 'shared/utils/timezone'

const REFETCH_INTERVAL = 15_000

const createAlreadyCreatedBookingsEvents = (
    booking: Booking,
    currentFormOrderId: number | null,
    activeBookingId: number | null,
): CalendarEvent => {
    const counterpartyName = booking.counterparty.fullName

    const bookingStartTime = moment(booking.startTime)
    const bookingEndTime = moment(booking.endTime)
    const end = (
        bookingEndTime.hour() === 0 &&
        bookingEndTime.minute() === 0 &&
        bookingStartTime.day() === bookingEndTime.day() - 1
            ? bookingEndTime.subtract(1, 'minutes')
            : bookingEndTime
    ).toDate()

    return {
        bookingId: booking.id,
        comment: booking?.comment || null,
        orderId: booking.orderId,
        title: <div>{booking.area.name}</div>,
        counterpartyName,
        counterpartyPhone: booking.counterparty.phone,
        isCompany: booking.counterparty.type === CounterpartyType.Company,
        start: booking.startTime ?? new Date(),
        end: end ?? new Date(),
        resourceId: booking.area.id,
        resourceName: booking.area.name ?? '',
        isNew: false,
        paidStatus: booking.paidStatus,
        isEdit: activeBookingId
            ? activeBookingId === booking.id
            : currentFormOrderId === booking.orderId,
    }
}

const createCurrentFormBookingsEvents = (
    booking: OrderBooking,
    currentFormOrderId: number | null,
    activeBookingId: number | null,
    isNew: boolean,
    currentFormCounterparty?: Counterparty | null,
): CalendarEvent => {
    const contact = currentFormCounterparty?.contact
    const isCompany = currentFormCounterparty?.type === CounterpartyType.Company

    const personName = contact
        ? [
              contact?.firstName ?? '',
              contact?.surname ?? '',
              contact?.patronymic ?? '',
          ].join(' ')
        : null
    const companyName =
        currentFormCounterparty?.additionalData?.documents?.organizationName ??
        ''
    const counterpartyName = isCompany ? companyName : personName

    const bookingStartTime = booking.startTime
        ? moment(booking.startTime)
        : moment()
    const bookingEndTime = booking.endTime ? moment(booking.endTime) : moment()
    const end = (
        bookingEndTime.hour() === 0 &&
        bookingEndTime.minute() === 0 &&
        bookingStartTime.day() === bookingEndTime.day() - 1
            ? bookingEndTime.subtract(1, 'minutes')
            : bookingEndTime
    ).toDate()

    return {
        bookingId: booking?.id,
        comment: booking?.comment || null,
        orderId: currentFormOrderId,
        title: <div>{booking?.area?.name}</div>,
        counterpartyName,
        counterpartyPhone: currentFormCounterparty?.phone ?? null,
        isCompany,
        start: booking.startTime ?? new Date(),
        end: end ?? new Date(),
        resourceId: booking.area?.id ?? 0,
        resourceName: booking?.area?.name ?? '',
        isNew,
        isEdit: activeBookingId === booking.id,
    }
}

export const mergeTimeRanges = (ranges: TimePeriod[]): TimePeriod[] => {
    if (!ranges || ranges.length === 0) return []

    const result: TimePeriod[] = []
    let prev: TimePeriod | null = null

    for (const curr of ranges) {
        if (prev && prev.end === curr.start) {
            prev.end = curr.end
        } else {
            prev = { ...curr }
            result.push(prev)
        }
    }

    return result
}

export const useBuildCalendarEvents = (
    currentDate: Moment,
    view: ViewModeType,
    areas: Area[],
) => {
    const dispatch = useDispatch()

    const timeFilter = useSelector(selectTimeFilter)
    const currentFormCounterparty = useSelector(selectBookingCounterparty)

    const currentFormOrderId = useSelector(selectBookingOrderId)

    const activeBookingId = useSelector(selectActiveBookingId)
    const { bookings: alreadyCreatedBookings, refetch } =
        useGetBookingsListQuery(timeFilter, {
            refetchInterval: REFETCH_INTERVAL,
        })

    useEffect(() => {
        refetch().then()
    }, [currentFormOrderId])

    const alreadyCreatedBookingsEvents = useMemo(
        () =>
            alreadyCreatedBookings.map(booking =>
                createAlreadyCreatedBookingsEvents(
                    booking,
                    currentFormOrderId,
                    activeBookingId,
                ),
            ),
        [alreadyCreatedBookings, activeBookingId, currentFormOrderId],
    )

    const currentFormBookings = useSelector(selectBookings)
    const currentFormBookingsEvents = useMemo(
        () =>
            currentFormBookings.map(booking =>
                createCurrentFormBookingsEvents(
                    booking,
                    currentFormOrderId,
                    activeBookingId,
                    !alreadyCreatedBookings.find(bkg => bkg.id === booking.id),
                    currentFormCounterparty,
                ),
            ),
        [
            alreadyCreatedBookings,
            currentFormBookings,
            currentFormOrderId,
            currentFormCounterparty,
            activeBookingId,
        ],
    )

    const bookingEvents = useMemo(
        () =>
            Array.from(
                new Map(
                    [
                        ...alreadyCreatedBookingsEvents,
                        ...currentFormBookingsEvents,
                    ].map(event => [event.bookingId, event]),
                ).values(),
            ),
        [alreadyCreatedBookingsEvents, currentFormBookingsEvents],
    )

    const splitBookingEvents = useMemo(() => {
        const result: CalendarEvent[] = []

        bookingEvents.forEach(booking => {
            if (!booking.start || !booking.end) {
                return null
            }

            const start = moment(booking.start)
            const end = moment(booking.end)

            let currentStart = start.clone()

            while (currentStart.isBefore(end)) {
                const endOfCurrentDay = currentStart.clone().endOf('day')
                const currentEnd = moment.min(end, endOfCurrentDay)

                result.push({
                    ...booking,
                    id: crypto.randomUUID(),
                    start: currentStart.toDate(),
                    end: currentEnd.toDate(),
                })

                currentStart = endOfCurrentDay
                    .clone()
                    .add(1, 'millisecond')
                    .startOf('day')
            }
        })

        return result
    }, [bookingEvents])

    useEffect(() => {
        dispatch(setCurrentCalendarEvents({ events: bookingEvents }))
    }, [bookingEvents])

    const bookingUnavailableEvents = useMemo(() => {
        const result: CalendarEvent[] = []

        if (view !== ViewMode.Day) {
            return []
        }

        areas.forEach(area => {
            if (!area.adBookingInfo?.availableBookingTime?.length) {
                return false
            }

            const availableBookingTime = mergeTimeRanges(
                area.adBookingInfo?.availableBookingTime,
            )

            availableBookingTime.forEach(availableTime => {
                if (!availableTime.start || !availableTime.end) {
                    return false
                }

                const date = currentDate.format('YYYY-MM-DD')
                const start = utlToLocal(moment(), availableTime.start).format(
                    'HH:mm',
                )
                const end = utlToLocal(moment(), availableTime.end).format(
                    'HH:mm',
                )

                const availableTimeStart = moment(
                    `${date} ${start}`,
                    'YYYY-MM-DD HH:mm',
                )
                const availableTimeEnd = moment(
                    `${date} ${end}`,
                    'YYYY-MM-DD HH:mm',
                )

                if (availableTimeStart.isSameOrAfter(availableTimeEnd)) {
                    const midEvent = {
                        bookingId: -1,
                        comment: '',
                        orderId: -1,
                        title: 'Бронь недоступна',
                        counterpartyName: '',
                        counterpartyPhone: '',
                        isCompany: false,
                        resourceId: area.id,
                        resourceName: area.name,
                        isNew: false,
                        isEdit: false,
                        isBlocked: true,
                        start: availableTimeEnd.toDate(),
                        end: availableTimeStart.toDate(),
                    }

                    if (
                        !isAlreadyHaveEventWithThatTime(
                            availableTimeEnd.toDate(),
                            availableTimeStart.toDate(),
                            area.id,
                            bookingEvents,
                            areas,
                            null,
                            false,
                        )
                    ) {
                        result.push(midEvent)
                        return
                    }
                }

                const startOFDay = moment(currentDate.startOf('day').toDate())
                const endOFDay = moment(currentDate.endOf('day').toDate())

                const startEvent = {
                    bookingId: -1,
                    comment: '',
                    orderId: -1,
                    title: 'Бронь недоступна',
                    counterpartyName: '',
                    counterpartyPhone: '',
                    isCompany: false,
                    resourceId: area.id,
                    resourceName: area.name,
                    isNew: false,
                    isEdit: false,
                    isBlocked: true,
                    start: startOFDay.toDate(),
                    end: availableTimeStart.toDate(),
                }

                if (
                    !(
                        isAlreadyHaveEventWithThatTime(
                            startOFDay.toDate(),
                            availableTimeStart.toDate(),
                            area.id,
                            bookingEvents,
                            areas,
                            null,
                            false,
                        ) && moment().subtract(1, 'day').isBefore(startOFDay)
                    )
                ) {
                    result.push(startEvent)
                }

                const endEvent = {
                    bookingId: -1,
                    comment: '',
                    orderId: -1,
                    title: 'Бронь недоступна',
                    counterpartyName: '',
                    counterpartyPhone: '',
                    isCompany: false,
                    resourceId: area.id,
                    resourceName: area.name,
                    isNew: false,
                    isEdit: false,
                    isBlocked: true,
                    start: availableTimeEnd.toDate(),
                    end: endOFDay.toDate(),
                }
                if (
                    !(
                        isAlreadyHaveEventWithThatTime(
                            availableTimeEnd.toDate(),
                            endOFDay.toDate(),
                            area.id,
                            bookingEvents,
                            areas,
                            null,
                            false,
                        ) &&
                        moment().subtract(1, 'day').isBefore(availableTimeEnd)
                    )
                ) {
                    result.push(endEvent)
                }
            })
        })
        return result
    }, [currentDate, view, areas])

    const blockingEvents = useMemo(() => {
        const result: CalendarEvent[] = []

        bookingEvents.forEach(booking => {
            const area = areas.find(a => a.id === booking.resourceId)

            if (!area) {
                return null
            }

            const blockingBeforeMinutes =
                area.adBookingInfo?.blockingBeforeMinutes
            if (blockingBeforeMinutes) {
                const event = {
                    bookingId: -1,
                    comment: '',
                    orderId: -1,
                    title: 'Бронь недоступна',
                    counterpartyName: '',
                    counterpartyPhone: '',
                    isCompany: false,
                    resourceId: area.id,
                    resourceName: area.name,
                    isNew: false,
                    isEdit: false,
                    isBlocked: false,
                    isBlockedByAreaPolicy: true,
                    start: moment(booking.start)
                        .clone()
                        .subtract(blockingBeforeMinutes, 'minutes')
                        .toDate(),
                    end: booking.start,
                }

                if (
                    !isAlreadyHaveEventWithThatTime(
                        moment(booking.start)
                            .clone()
                            .subtract(blockingBeforeMinutes, 'minutes')
                            .toDate(),
                        booking.start!,
                        area.id,
                        bookingUnavailableEvents,
                        areas,
                        null,
                        false,
                    )
                ) {
                    result.push(event)
                }
            }

            const blockingAfterMinutes =
                area.adBookingInfo?.blockingAfterMinutes
            if (blockingAfterMinutes) {
                const event = {
                    bookingId: -1,
                    comment: '',
                    orderId: -1,
                    title: 'Бронь недоступна',
                    counterpartyName: '',
                    counterpartyPhone: '',
                    isCompany: false,
                    resourceId: area.id,
                    resourceName: area.name,
                    isNew: false,
                    isEdit: false,
                    isBlocked: false,
                    isBlockedByAreaPolicy: true,
                    start: booking.end,
                    end: moment(booking.end)
                        .clone()
                        .add(blockingAfterMinutes, 'minutes')
                        .toDate(),
                }

                if (
                    !isAlreadyHaveEventWithThatTime(
                        booking.end!,
                        moment(booking.end)
                            .clone()
                            .add(blockingAfterMinutes, 'minutes')
                            .toDate(),
                        area.id,
                        bookingUnavailableEvents,
                        areas,
                        null,
                        false,
                    )
                ) {
                    result.push(event)
                }
            }
        })

        return result
    }, [bookingEvents, areas])

    return {
        blockingEvents,
        backgroundEvents: bookingUnavailableEvents,
        alreadyCreatedBookingsEvents,
        currentFormBookingsEvents,
        bookingEvents: splitBookingEvents,
    }
}
